'use client';

import { AppDownload } from '@repo/svgs';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { Button } from '@/components/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { usePromptContext } from '@/providers/PromptProvider';

/**
 * Deferred deep link — routes to the right store by device.
 */
const APP_LINK = 'https://links.openigloo.com/Eqsnv/7eut/lyj7';

interface AppDownloadPromptProps {
  open: boolean;
}

/**
 * App-download prompt. Fires on the 1st home view, mobile web only.
 * The provider gates on the viewport before scheduling, so this never
 * opens above `md`.
 *
 * Shell is cloned from `ReportPrompt`.
 */
export function AppDownloadPrompt({ open }: AppDownloadPromptProps) {
  const analytics = useAnalytics();
  const { dismissActivePrompt, markPromptShown } = usePromptContext();

  const handleGetApp = () => {
    analytics.track('app_download_prompt_get_app');
    markPromptShown('appDownload');
    dismissActivePrompt();
    window.open(APP_LINK, '_blank', 'noopener,noreferrer');
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={dismissActivePrompt}
      // Empty label so the AppBar header renders as an X-only bar
      // (matches the other prompts — Modal.tsx renders AppBar whenever
      // `onClose` is provided). Never a floating cross.
      label=""
      maxWidth="md"
      closeOnOverlayClick
      contentClassName="rounded-3xl"
      desktopBodyClassName="rounded-3xl"
      // Strip the AppBar's built-in grey divider under the X — this
      // modal is header-less by design.
      crossPlatformClassName="[&_[role=banner]]:!border-b-0"
    >
      {/* `-mt-4` negates ResponsiveModal's top py-4 so the image sits
          flush against the AppBar. `md:pb-2` tops up the wrapper's 16px
          bottom padding to the 24px desktop spec (mobile keeps 16).
          Gap 4 between items. */}
      <div className="flex flex-col gap-4 -mt-4 md:pb-2">
        {/* Image spans full content width — left edge aligns with the
            title (both sit at ResponsiveModal's px-6 inset). */}
        <AppDownload className="w-full h-auto" />
        <div className="flex flex-col gap-2">
          <h6 className="text-heading-6 font-semibold text-primary text-left">
            Renting is better in the app
          </h6>
          <p className="text-body-2 text-primary text-left">
            Faster search, instant alerts, and more.
          </p>
        </div>
        <Button variant="primary" size="md" fullWidth onClick={handleGetApp}>
          Get the Openigloo app
        </Button>
      </div>
    </ResponsiveModal>
  );
}
